import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ShoppingCart, Trash2, Plus, Minus, CheckCircle, ChevronLeft, RefreshCw, X, AlertCircle, Banknote, CreditCard, Smartphone, ShoppingBag, Clock, ArrowRight, type LucideIcon } from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface KioskProduct {
  id: string;
  name: string;
  salePrice: number;
  imageUrl: string | null;
  stockQuantity: number;
  trackStock: boolean;
  category: { id: string; name: string } | null;
}

interface CartItem {
  product: KioskProduct;
  quantity: number;
}

type KioskStep = 'browse' | 'cart' | 'payment' | 'thankyou' | 'error';

/** POST /sales body (axios `response.data` — full created sale). */
interface KioskCheckoutSale {
  invoiceNumber?: string;
  totalAmount?: number | string;
  grandTotal?: number | string;
  total?: number | string;
}

function resolveCheckoutTotal(data: KioskCheckoutSale, fallback: number): number {
  if (data.totalAmount != null && data.totalAmount !== '') {
    const n = Number(data.totalAmount);
    if (!Number.isNaN(n)) return n;
  }
  if (data.grandTotal != null && data.grandTotal !== '') {
    const n = Number(data.grandTotal);
    if (!Number.isNaN(n)) return n;
  }
  if (data.total != null && data.total !== '') {
    const n = Number(data.total);
    if (!Number.isNaN(n)) return n;
  }
  return fallback;
}

const PAYMENT_METHODS: { value: string; label: string; Icon: LucideIcon; subtitle: string }[] = [
  { value: 'CASH', label: 'Cash', Icon: Banknote, subtitle: 'Pay with cash at counter' },
  { value: 'CARD', label: 'Card', Icon: CreditCard, subtitle: 'Debit or credit card' },
  { value: 'MOBILE_WALLET', label: 'Mobile Wallet', Icon: Smartphone, subtitle: 'JazzCash, Easypaisa, etc.' },
];

const IDLE_TIMEOUT_MS = 60_000; // reset after 60s idle

export function KioskPage() {
  const { tenant } = useAuthStore();
  const { selectedLocationId } = useLocationStore();

  const [step, setStep] = useState<KioskStep>('browse');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('CASH');
  const [search, setSearch] = useState('');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [thankYouInvoice, setThankYouInvoice] = useState('');
  const [thankYouTotal, setThankYouTotal] = useState(0);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepRef = useRef<KioskStep>(step);
  const checkoutTotalFallbackRef = useRef(0);
  const [idleWarning, setIdleWarning] = useState(false);

  stepRef.current = step;

  const resetKiosk = useCallback(() => {
    setCart([]);
    setStep('browse');
    setSearch('');
    setSelectedCategory('');
    setPaymentMethod('CASH');
    setThankYouInvoice('');
    setThankYouTotal(0);
    setIdleWarning(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (idleResetTimer.current) clearTimeout(idleResetTimer.current);
  }, []);

  // Idle reset timer (skipped on thank-you — guest must tap Done)
  const resetIdleTimer = useCallback(() => {
    if (stepRef.current === 'thankyou') return;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (idleResetTimer.current) clearTimeout(idleResetTimer.current);
    setIdleWarning(false);
    idleTimer.current = setTimeout(() => {
      if (stepRef.current === 'thankyou') return;
      setIdleWarning(true);
      idleResetTimer.current = setTimeout(() => {
        if (stepRef.current !== 'thankyou') resetKiosk();
      }, 10_000);
    }, IDLE_TIMEOUT_MS);
  }, [resetKiosk]);

  useEffect(() => {
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [resetIdleTimer]);

  // Auto-recover after error screen (30s — long enough for staff to read)
  useEffect(() => {
    if (step === 'error') {
      const t = setTimeout(() => {
        setCheckoutError(null);
        setStep('payment');
      }, 30_000);
      return () => clearTimeout(t);
    }
  }, [step]);

  const { data: categoriesData } = useQuery({
    queryKey: ['kiosk-categories'],
    // Only categories that have a sellable product, so an all-raw-materials category
    // is never an empty rail button on the guest kiosk (T-238).
    queryFn: () => api.get('/categories', { params: { sellable: true } }).then((r) => r.data),
    staleTime: 5 * 60_000,
  });

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['kiosk-products', selectedCategory, search],
    queryFn: () =>
      api.get('/products/pos', {
        params: {
          categoryId: selectedCategory || undefined,
          search: search || undefined,
          limit: 60,
        },
      }).then((r) => r.data?.data ?? r.data ?? []),
    staleTime: 2 * 60_000,
  });

  const checkoutMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post<KioskCheckoutSale>('/sales', payload).then((r) => r.data),
    onSuccess: (data) => {
      setCheckoutError(null);
      setThankYouInvoice(data.invoiceNumber ?? '');
      setThankYouTotal(resolveCheckoutTotal(data, checkoutTotalFallbackRef.current));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (idleResetTimer.current) clearTimeout(idleResetTimer.current);
      setIdleWarning(false);
      setStep('thankyou');
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      const msg = err.response?.data?.message ?? 'Checkout failed. Please try again or ask staff for help.';
      setCheckoutError(msg);
      setStep('error');
    },
  });

  const addToCart = (product: KioskProduct) => {
    if (product.trackStock && product.stockQuantity <= 0) return;
    setCart((prev) => {
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQty = (productId: string, delta: number) => {
    setCart((prev) => {
      const updated = prev
        .map((i) => (i.product.id === productId ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0);
      return updated;
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((i) => i.product.id !== productId));
  };

  const cartTotal = cart.reduce((sum, i) => sum + i.product.salePrice * i.quantity, 0);
  const cartItemCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  const handleCheckout = () => {
    checkoutTotalFallbackRef.current = cartTotal;
    const payload = {
      items: cart.map((i) => ({
        productId: i.product.id,
        quantity: i.quantity,
        unitPrice: i.product.salePrice,
      })),
      payments: [{ method: paymentMethod, amount: cartTotal }],
      locationId: selectedLocationId,
      notes: 'Kiosk self-checkout',
    };
    checkoutMutation.mutate(payload);
  };

  const categories: { id: string; name: string }[] = categoriesData ?? [];
  const products: KioskProduct[] = productsData ?? [];

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col pb-[env(safe-area-inset-bottom)]" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Idle warning overlay */}
      {idleWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80">
          <div className="bg-card rounded-2xl p-10 text-center max-w-sm">
            <Clock className="h-12 w-12 text-warning-text mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-2xl font-bold mb-2">Still there?</h2>
            <p className="text-muted-foreground mb-6">Session will reset in a few seconds due to inactivity.</p>
            <Button onClick={resetIdleTimer} size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg">
              Continue Shopping
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 bg-card border-b border-border">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold tracking-tight">{tenant?.name ?? 'Welcome'}</div>
          {step !== 'browse' && step !== 'thankyou' && (
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground"
              onClick={() => setStep(step === 'payment' ? 'cart' : 'browse')}>
              <ChevronLeft className="h-5 w-5 me-1" /> Back
            </Button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {step === 'browse' && (
            <div className="relative">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products…"
                className="rounded-xl bg-muted px-4 py-2.5 w-56"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Cart button */}
          {step === 'browse' && (
            <Button
              onClick={() => { if (cart.length > 0) setStep('cart'); }}
              disabled={cart.length === 0}
              className="relative bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-5 py-2.5 text-base font-semibold gap-2"
            >
              <ShoppingCart className="h-5 w-5" />
              View Cart
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -end-2 bg-destructive text-destructive-foreground text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                  {cartItemCount}
                </span>
              )}
            </Button>
          )}

          <Button variant="ghost" size="sm" onClick={resetKiosk} className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden flex flex-col">

        {/* ── BROWSE STEP ── */}
        {step === 'browse' && (
          <div className="flex-1 flex overflow-hidden">
            {/* Category sidebar */}
            <div className="w-48 flex-shrink-0 bg-card border-e border-border overflow-y-auto">
              <div
                onClick={() => setSelectedCategory('')}
                className={`cursor-pointer px-4 py-4 min-h-[52px] flex items-center text-base font-medium transition-colors border-b border-border ${!selectedCategory ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                All Products
              </div>
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`cursor-pointer px-4 py-4 min-h-[52px] flex items-center text-base font-medium transition-colors border-b border-border ${selectedCategory === cat.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                >
                  {cat.name}
                </div>
              ))}
            </div>

            {/* Product grid */}
            <div className="flex-1 overflow-y-auto p-6">
              {productsLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="bg-muted rounded-2xl aspect-square animate-pulse" />
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-4" aria-hidden="true" />
                  <p className="text-xl">No products found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {products.map((product) => {
                    const outOfStock = product.trackStock && product.stockQuantity <= 0;
                    const inCart = cart.find((i) => i.product.id === product.id);
                    return (
                      <button
                        key={product.id}
                        onClick={() => addToCart(product)}
                        disabled={outOfStock}
                        className={`relative flex flex-col rounded-2xl overflow-hidden border transition-all text-start group
                          ${outOfStock ? 'opacity-40 cursor-not-allowed border-border bg-card' : 'border-border bg-muted hover:border-primary hover:bg-accent active:scale-95 cursor-pointer'}
                          ${inCart ? 'border-primary ring-2 ring-primary/30' : ''}`}
                      >
                        {/* Product image or placeholder */}
                        <div className="aspect-square bg-secondary flex items-center justify-center overflow-hidden">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <ShoppingBag className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                          )}
                        </div>
                        <div className="p-3 flex-1 flex flex-col justify-between">
                          <p className="text-base font-semibold text-foreground line-clamp-2 leading-tight">{product.name}</p>
                          <p className="text-primary-text font-bold text-lg mt-1">{formatCurrency(product.salePrice)}</p>
                        </div>
                        {inCart && (
                          <div className="absolute top-2 end-2 bg-primary text-primary-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                            {inCart.quantity}
                          </div>
                        )}
                        {outOfStock && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                            <span className="text-xs text-muted-foreground font-medium">Out of stock</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── CART STEP ── */}
        {step === 'cart' && (
          <div className="flex-1 overflow-y-auto p-8 max-w-2xl mx-auto w-full">
            <h2 className="text-2xl font-bold mb-6">Your Order</h2>
            {cart.length === 0 ? (
              <div className="text-center text-muted-foreground mt-20">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4" aria-hidden="true" />
                <p className="text-xl">Your cart is empty</p>
                <Button className="mt-6" onClick={() => setStep('browse')}>Browse Products</Button>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-8">
                  {cart.map((item) => (
                    <div key={item.product.id} className="flex items-center gap-4 bg-muted rounded-2xl p-4 border border-border">
                      <div className="w-16 h-16 rounded-xl bg-secondary flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {item.product.imageUrl ? (
                          <img src={item.product.imageUrl} alt={item.product.name} className="w-full h-full object-cover" />
                        ) : (
                          <ShoppingBag className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{item.product.name}</p>
                        <p className="text-primary-text font-semibold">{formatCurrency(item.product.salePrice)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(item.product.id, -1)} aria-label="Decrease quantity"
                          className="w-12 h-12 min-h-[48px] min-w-[48px] rounded-full bg-secondary hover:bg-accent flex items-center justify-center text-foreground font-bold transition-colors active:scale-95">
                          <Minus className="h-5 w-5" />
                        </button>
                        <span className="w-10 text-center text-foreground font-bold text-xl">{item.quantity}</span>
                        <button onClick={() => updateQty(item.product.id, 1)} aria-label="Increase quantity"
                          className="w-12 h-12 min-h-[48px] min-w-[48px] rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center text-primary-foreground font-bold transition-colors active:scale-95">
                          <Plus className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="w-20 text-end font-bold text-foreground">{formatCurrency(item.product.salePrice * item.quantity)}</div>
                      <button onClick={() => removeFromCart(item.product.id)} aria-label="Remove item"
                        className="ms-2 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive-text transition-colors">
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="bg-muted rounded-2xl p-6 border border-border">
                  <div className="flex justify-between items-center text-lg font-bold text-foreground mb-6">
                    <span>Total</span>
                    <span className="text-primary text-2xl">{formatCurrency(cartTotal)}</span>
                  </div>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-xl py-6 rounded-2xl font-bold"
                    onClick={() => setStep('payment')}>
                    Proceed to Payment <ArrowRight className="h-5 w-5 ms-2" aria-hidden="true" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── PAYMENT STEP ── */}
        {step === 'payment' && (
          <div className="flex-1 overflow-y-auto p-8 max-w-lg mx-auto w-full">
            <h2 className="text-2xl font-bold mb-2">How would you like to pay?</h2>
            <p className="text-muted-foreground mb-8">Total: <span className="text-foreground font-bold text-xl">{formatCurrency(cartTotal)}</span></p>

            <div className="space-y-3 mb-8">
              {PAYMENT_METHODS.map((pm) => (
                <button key={pm.value} onClick={() => setPaymentMethod(pm.value)}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 transition-all text-start
                    ${paymentMethod === pm.value ? 'border-primary bg-primary/10' : 'border-border bg-muted hover:border-primary/40'}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-background/80 border border-border/50">
                    <pm.Icon className="h-5 w-5 text-foreground" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-semibold text-foreground text-lg">{pm.label}</p>
                    <p className="text-sm text-muted-foreground">{pm.subtitle}</p>
                  </div>
                  {paymentMethod === pm.value && (
                    <CheckCircle className="h-6 w-6 text-primary ms-auto" />
                  )}
                </button>
              ))}
            </div>

            <Button
              className="w-full bg-success-solid hover:bg-success-solid/90 text-success-solid-foreground text-xl py-6 rounded-2xl font-bold"
              disabled={checkoutMutation.isPending}
              onClick={handleCheckout}
            >
              {checkoutMutation.isPending ? (
                <span className="flex items-center gap-2 justify-center"><RefreshCw className="h-5 w-5 animate-spin" /> Processing…</span>
              ) : `Confirm Order — ${formatCurrency(cartTotal)}`}
            </Button>

            {checkoutError && (
              <div className="mt-4 flex items-start gap-3 bg-destructive/10 border border-destructive/40 rounded-2xl p-4">
                <AlertCircle className="h-6 w-6 text-destructive-text flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-destructive-text">Order could not be placed</p>
                  <p className="text-sm text-destructive-text mt-1">{checkoutError}</p>
                  <button
                    className="mt-2 text-sm text-destructive-text underline"
                    onClick={() => setCheckoutError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ERROR STEP ── */}
        {step === 'error' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <AlertCircle className="h-24 w-24 text-destructive-text mb-6" />
            <h1 className="text-4xl font-bold text-foreground mb-4">Oops — something went wrong</h1>
            <p className="text-xl text-muted-foreground mb-2">Your order could not be placed.</p>
            {checkoutError && (
              <p className="text-base text-destructive-text mt-2 max-w-xl">{checkoutError}</p>
            )}
            <p className="text-muted-foreground mt-6 mb-10">Please try again, or ask a staff member for help.</p>
            <div className="flex gap-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-10 py-4 rounded-2xl"
                onClick={() => { setCheckoutError(null); setStep('payment'); }}
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                className="text-lg px-10 py-4 rounded-2xl border-border text-muted-foreground hover:bg-muted"
                onClick={resetKiosk}
              >
                Start Over
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-6">Returning to payment in 30 seconds…</p>
          </div>
        )}

        {/* ── THANK YOU STEP ── */}
        {step === 'thankyou' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <CheckCircle className="h-24 w-24 text-success-text mb-6" />
            <h1 className="text-4xl font-bold text-foreground mb-4">Thank you!</h1>
            <p className="text-xl text-muted-foreground mb-6">Your order has been placed successfully.</p>
            {thankYouInvoice ? (
              <p className="text-2xl font-semibold text-foreground mb-3">
                Order number: <span className="text-primary-text">{thankYouInvoice}</span>
              </p>
            ) : null}
            <p className="text-xl text-muted-foreground mb-2">
              Amount to pay at counter:{' '}
              <span className="font-bold text-foreground">{formatCurrency(thankYouTotal)}</span>
            </p>
            <p className="text-muted-foreground mb-10">Please proceed to the counter for payment/pickup.</p>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-10 py-4 rounded-2xl" onClick={resetKiosk}>
              Done
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 py-3 bg-card border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Self-service kiosk · {tenant?.name}</span>
        <span>Touch a product to add it to your cart</span>
      </div>
    </div>
  );
}
